import { PERMISSIONS } from "modules/PermissionsConstants";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useGetSupplyAlertProfileList from "../hooks/useGetSupplyAlertProfileList";
import useDeleteSupplyAlertProfile from "../hooks/useDeleteSupplyAlertProfile";
import { ISupplyAlertProfile } from "../types";
import Timer from "components/Timer";
import { toast } from "components/toastNotification";
import { useLocation } from "react-router-dom";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import {
  addStateSupplyAlertProfile,
  editStateSupplyAlertProfile,
  supplyAlertProfileFetching,
  supplyAlertProfileFilterText,
  rowEditSupplyAlertProfile,
  selectedRowsStateSupplyAlertProfile,
} from "../atoms/atoms";

import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import EditButton from "components/EditButton";
import TableFilters from "components/TableFilters";
import AddButton from "components/AddButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import SupplyAlertProfileForm from "../form/SupplyAlertProfileForm";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";

export const SupplyAlertProfileTable = ({ }) => {
  const { data: directData, refetch: reFetch } = useGetSupplyAlertProfileList();

  const BulkDelete = useDeleteSupplyAlertProfile();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateSupplyAlertProfile);
  const [selectedRows, setSelectedRows] = useRecoilState(selectedRowsStateSupplyAlertProfile);
  const [actionEditState, setActionEditState] = useRecoilState(editStateSupplyAlertProfile);
  const [filterTextState, setFilterTextState] = useRecoilState(supplyAlertProfileFilterText);
  const setEditRowData = useSetRecoilState(rowEditSupplyAlertProfile);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(supplyAlertProfileFetching);

  //Local States
  const [tableData, setTableData] = useState<ISupplyAlertProfile[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<ISupplyAlertProfile[]>([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_leasedequipment_supplyalertprofile_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_leasedequipment_supplyalertprofile_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_leasedequipment_supplyalertprofile_edit;

  //For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  //For Filter/Search
  const filtereditems = tableData && tableData?.filter((item) =>
    item.supplyAlertProfileName &&
    item.supplyAlertProfileName
      .toLowerCase()
      .includes(filterTextState?.trim().toLowerCase())
  );

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${rowsCount > 1 ? "Supply Alert Profile/s" : "Supply Alert Profile"
      }?`}</div>
  );

  const matColumns = [
    {
      title: "Name",
      field: "supplyAlertProfileName",
      customSort: (a, b) =>
        a.supplyAlertProfileName && b.supplyAlertProfileName
          ? a.supplyAlertProfileName.trim().localeCompare(b.supplyAlertProfileName.trim())
          : a.supplyAlertProfileName
            ? 0
            : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
            ? 0
            : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
            ? 0
            : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => handleEdit(rowData)}
            />
          </div>
        );
      },
    },
  ];

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleGetFilteredData = () => {
    notificationFilterById(
      recordIdFromRecent.recordId,
      recordIdFromRecent.moduleName,
      recordIdFromRecent.subModuleName
    ).then((result: ISupplyAlertProfile[] | undefined) => {
      if (typeof result !== "object") {
        toast.fire("Failed", result, "error");
      } else {
        setTableFilteredData(result);
        setRecent(result[0].supplyAlertProfileName);
      }
    });
  }

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.supplyAlertProfileId);
    BulkDelete(ids);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
    setSelectedRows([]);

    if (recordIdFromRecent) handleGetFilteredData();
  };

  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  useEffect(() => {
    if (recordIdFromRecent) {
      handleGetFilteredData();
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  useEffect(() => {
    if (recent !== "") {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <SupplyAlertProfileForm reFetch={reFetch} setTableFilteredData={setTableFilteredData} />
        </div>
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              recentText={recent}
              setRecent={setRecent}
              textInputName={"SupplyAlertProfileName"}
              columnDetails={"Supply Alert Profile Name"}
              filterText={setFilterTextState}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div>
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Supply Alert Profile"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                    ? tableData
                    : filterTextState === ""
                      ? tableFilteredData
                      : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Supply Alert Profile"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default SupplyAlertProfileTable;
